/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * How the subject is compared: exact | wildcard.
 */
export const WorkloadAdmissionMatchKind = {
  Exact: "exact",
  Wildcard: "wildcard",
} as const;
/**
 * How the subject is compared: exact | wildcard.
 */
export type WorkloadAdmissionMatchKind = ClosedEnum<
  typeof WorkloadAdmissionMatchKind
>;

/**
 * A subject an organization admits, with the agent it inherits its policy from.
 */
export type WorkloadAdmission = {
  /**
   * The agent whose policy this workload inherits. Empty when the assignment is missing, which the token endpoint refuses.
   */
  agentId: string;
  /**
   * The assigned agent's name; empty when the assignment is missing.
   */
  agentName: string;
  createdAt: Date;
  /**
   * The admission id.
   */
  id: string;
  /**
   * The issuer identifier, denormalized so a list does not need a second lookup.
   */
  issuer: string;
  /**
   * The issuer's operator-facing label.
   */
  issuerName: string;
  /**
   * How the subject is compared: exact | wildcard.
   */
  matchKind: WorkloadAdmissionMatchKind;
  /**
   * Optional label; empty when none was supplied.
   */
  name: string;
  /**
   * The owning organization id.
   */
  organizationId: string;
  /**
   * The owning project id; empty for an organization-tier admission.
   */
  projectId: string;
  /**
   * The sub claim, exactly as stored.
   */
  subject: string;
  /**
   * Free-form labels for finding the admitted workload. Empty rather than absent where none are set.
   */
  tags: Array<string>;
  updatedAt: Date;
  /**
   * False when this is a wildcard rule under an issuer that no longer permits wildcard matching. Such a rule is inert: it is stored, listed, and matches nothing.
   */
  wildcardActive: boolean;
  /**
   * The issuer that must assert this subject.
   */
  workloadIssuerId: string;
};

/** @internal */
export const WorkloadAdmissionMatchKind$inboundSchema: z.ZodMiniEnum<
  typeof WorkloadAdmissionMatchKind
> = z.enum(WorkloadAdmissionMatchKind);

/** @internal */
export const WorkloadAdmission$inboundSchema: z.ZodMiniType<
  WorkloadAdmission,
  unknown
> = z.pipe(
  z.object({
    agent_id: z.string(),
    agent_name: z.string(),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    id: z.string(),
    issuer: z.string(),
    issuer_name: z.string(),
    match_kind: WorkloadAdmissionMatchKind$inboundSchema,
    name: z.string(),
    organization_id: z.string(),
    project_id: z.string(),
    subject: z.string(),
    tags: z.array(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    wildcard_active: z.boolean(),
    workload_issuer_id: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "agent_id": "agentId",
      "agent_name": "agentName",
      "created_at": "createdAt",
      "issuer_name": "issuerName",
      "match_kind": "matchKind",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "updated_at": "updatedAt",
      "wildcard_active": "wildcardActive",
      "workload_issuer_id": "workloadIssuerId",
    });
  }),
);

export function workloadAdmissionFromJSON(
  jsonString: string,
): SafeParseResult<WorkloadAdmission, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => WorkloadAdmission$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'WorkloadAdmission' from JSON`,
  );
}
